import React, { useEffect, useState } from 'react'
import { User } from '@/types'
import { ApkInfo } from '@/types/apk.types'
import { apkService } from '@/services/apk.service'
import { Modal } from '@/components/ui/Modal'
import { FileInput } from '@/components/ui/FileInput'
import { Button } from '@/components/ui/Button'
import { toastError, toastSuccess } from '@/utils/toast'
import { formatApkActorName, getApkSourceLabel } from '@/utils/apk-utils'
import { isAttachedReseller } from '@/utils/role-access'

interface ActorApkModalProps {
  actor: User
  canManage: boolean
  onClose: () => void
}

export const ActorApkModal: React.FC<ActorApkModalProps> = ({ actor, canManage, onClose }) => {
  const [apkInfo, setApkInfo] = useState<ApkInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const isAttached = isAttachedReseller(actor)
  const actorLabel =
    actor.companyName?.trim() || `${actor.firstname} ${actor.lastname}`.trim() || actor.email
  const readOnly = !canManage || isAttached

  useEffect(() => {
    let isMounted = true

    apkService
      .getActor(actor._id)
      .then(data => {
        if (isMounted) {
          setApkInfo(data)
        }
      })
      .catch(error => {
        toastError(error instanceof Error ? error.message : "Erreur lors du chargement de l'APK")
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [actor._id])

  const handleFileChange = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.apk')) {
      toastError('Seuls les fichiers .apk sont autorisés')
      return
    }

    setSelectedFile(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    try {
      const updated = await apkService.uploadActor(actor._id, selectedFile)
      setApkInfo(updated)
      setSelectedFile(null)
      toastSuccess('APK marque blanche mise à jour avec succès')
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Erreur lors du téléchargement de l'APK")
    } finally {
      setIsUploading(false)
    }
  }

  const handleClear = async () => {
    setIsClearing(true)
    try {
      const updated = await apkService.clearActor(actor._id)
      setApkInfo(updated)
      toastSuccess("L'APK marque blanche a été retirée — l'APK standard s'applique")
    } catch (error) {
      toastError(error instanceof Error ? error.message : "Erreur lors de la suppression de l'APK")
    } finally {
      setIsClearing(false)
    }
  }

  const inheritedName = apkInfo ? formatApkActorName(apkInfo) : null

  return (
    <Modal
      title={`APK assignée — ${actorLabel}`}
      onCancel={onClose}
      onConfirm={onClose}
      confirmButtonText="Fermer"
      showCancel={false}
      size="md"
    >
      {isLoading ? (
        <p className="text-sm text-[var(--color-text-secondary)]">Chargement...</p>
      ) : (
        <div className="space-y-4">
          {isAttached && (
            <p className="text-sm text-[var(--color-text-secondary)]">
              Ce revendeur rattaché utilise l&apos;APK de son grossiste. La gestion se fait au
              niveau du compte grossiste.
            </p>
          )}

          <div className="p-3 bg-[var(--color-gray-100)] rounded-lg text-sm text-[var(--color-text-primary)] space-y-1">
            {apkInfo?.filename ? (
              <>
                <p>
                  Fichier : <span className="font-medium">{apkInfo.filename}</span>
                </p>
                <p className="text-[var(--color-text-secondary)]">
                  {getApkSourceLabel(apkInfo.source)}
                  {inheritedName ? ` (${inheritedName})` : ''}
                </p>
              </>
            ) : (
              <p className="text-[var(--color-text-muted)]">Aucune APK disponible</p>
            )}
          </div>

          {!readOnly && (
            <>
              <FileInput
                label="Téléverser une APK marque blanche"
                accept=".apk,application/vnd.android.package-archive"
                onChange={handleFileChange}
                disabled={isUploading || isClearing}
                selectedFiles={selectedFile ? [selectedFile] : []}
                onFileRemove={() => setSelectedFile(null)}
                helperText="Remplace la version précédente. Fichier .apk uniquement (max. 200 Mo)."
              />

              <div className="flex flex-wrap justify-end gap-2">
                {apkInfo?.source === 'custom' && (
                  <Button
                    variant="outline"
                    onClick={handleClear}
                    isLoading={isClearing}
                    disabled={isUploading}
                  >
                    Revenir à l&apos;APK standard
                  </Button>
                )}
                <Button
                  onClick={handleUpload}
                  isLoading={isUploading}
                  disabled={!selectedFile || isClearing}
                >
                  Enregistrer l&apos;APK
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
